import { D2, output } from '@tanstack/db-ivm'
import { createSourceRecord } from '../utils/source-record.js'
import { createDeferred } from '../deferred.js'
import { runAllCallbacks } from '../utils/callbacks.js'
import { normalizeError } from '../utils/error.js'
import { deepEquals } from '../utils.js'
import { codedMessage, devBuild } from '../error-message.js'
import { compileQuery } from './compiler/index.js'
import { normalizeExpressionPaths } from './compiler/expressions.js'
import { getCollectionBuilder } from './live/collection-registry.js'
import { scheduleQueryGraphRun } from './live/graph-scheduler.js'
import {
  SubsetDemandController,
  hasPendingJoinedWork as hasPendingJoinedSourceWork,
} from './live/subset-demand-controller.js'
import { OrderedSourceLoader } from './live/ordered-source-loader.js'
import {
  buildQueryFromConfig,
  computeSubscriptionOrderByHints,
  extractCollectionFromSource,
  extractCollectionSources,
  extractCollectionsFromQuery,
  reconcileChangesForD2,
  sendChangesToInput,
  sourcePermitsInitialReadiness,
  splitUpdates,
} from './live/utils.js'
import type { RootStreamBuilder } from '@tanstack/db-ivm'
import type { Collection } from '../collection/index.js'
import type { CollectionSubscription } from '../collection/subscription.js'
import type { InitialQueryBuilder, QueryBuilder } from './builder/index.js'
import type { Context } from './builder/types.js'
import type { BasicExpression, QueryIR } from './ir.js'
import type { OrderByOptimizationInfo } from './compiler/order-by.js'
import type { GraphDependency } from './live/graph-scheduler.js'
import type {
  LazyCollectionCallbacks,
  LazyDemandPlan,
} from './compiler/joins.js'
import type {
  ChangeMessage,
  KeyedStream,
  StringCollationConfig,
} from '../types.js'

// ---------------------------------------------------------------------------
// Public Types
// ---------------------------------------------------------------------------

/** Event types for query result deltas */
export type DeltaType = 'enter' | 'exit' | 'update'

/** Delta event emitted when a row enters, exits, or updates within a query result */
export type DeltaEvent<
  TRow extends object = Record<string, unknown>,
  TKey extends string | number = string | number,
> =
  | {
      type: 'enter'
      key: TKey
      /** Current value for the entering row */
      value: TRow
      metadata?: Record<string, unknown>
    }
  | {
      type: 'exit'
      key: TKey
      /** Current value for the exiting row */
      value: TRow
      metadata?: Record<string, unknown>
    }
  | {
      type: 'update'
      key: TKey
      /** Current value after the update */
      value: TRow
      /** Previous value before the batch */
      previousValue: TRow
      metadata?: Record<string, unknown>
    }

/** Context passed to effect handlers */
export interface EffectContext {
  /** ID of this effect (auto-generated if not provided) */
  effectId: string
  /** Aborted when effect.dispose() is called */
  signal: AbortSignal
}

/** Query input - can be a builder function or a prebuilt query */
export type EffectQueryInput<TContext extends Context> =
  ((q: InitialQueryBuilder) => QueryBuilder<TContext>) | QueryBuilder<TContext>

type EffectEventHandler<
  TRow extends object = Record<string, unknown>,
  TKey extends string | number = string | number,
> = (event: DeltaEvent<TRow, TKey>, ctx: EffectContext) => void | Promise<void>

type EffectBatchHandler<
  TRow extends object = Record<string, unknown>,
  TKey extends string | number = string | number,
> = (
  events: Array<DeltaEvent<TRow, TKey>>,
  ctx: EffectContext,
) => void | Promise<void>

/** Effect configuration */
export interface EffectConfig<
  TRow extends object = Record<string, unknown>,
  TKey extends string | number = string | number,
> {
  /** Optional ID for debugging/tracing */
  id?: string

  /** Query to watch for deltas */
  query: EffectQueryInput<any>

  /** Called once for each row entering the query result */
  onEnter?: EffectEventHandler<TRow, TKey>

  /** Called once for each row updating within the query result */
  onUpdate?: EffectEventHandler<TRow, TKey>

  /** Called once for each row exiting the query result */
  onExit?: EffectEventHandler<TRow, TKey>

  /** Called once per graph run with all delta events from that batch */
  onBatch?: EffectBatchHandler<TRow, TKey>

  /** Error handler for exceptions thrown by effect callbacks */
  onError?: (error: Error, event: DeltaEvent<TRow, TKey>) => void

  /**
   * Called when a source collection enters an error or cleaned-up state.
   * The effect is automatically disposed after this callback fires.
   * If not provided, the error is logged to console.error.
   */
  onSourceError?: (error: Error) => void

  /**
   * Skip deltas during initial collection load.
   * Defaults to false (process all deltas including initial sync).
   * Set to true for effects that should only process new changes.
   */
  skipInitial?: boolean
}

/** Handle returned by createEffect */
export interface Effect {
  /**
   * Dispose the effect and await in-flight handlers. Calls during one cleanup
   * attempt, including calls from abort/release callbacks, share its outcome.
   */
  dispose: () => Promise<void>
  /** Whether this effect has been disposed */
  readonly disposed: boolean
}

// ---------------------------------------------------------------------------
// Internal Types
// ---------------------------------------------------------------------------

/** Accumulated changes for a single key within a graph run */
interface EffectChanges<T> {
  deletes: number
  inserts: number
  /** Value from the latest insert (the newest/current value) */
  insertValue?: T
  /** Value from the first delete (the oldest/previous value before the batch) */
  deleteValue?: T
}

// ---------------------------------------------------------------------------
// Global Counter
// ---------------------------------------------------------------------------

let effectCounter = 0

// ---------------------------------------------------------------------------
// createEffect
// ---------------------------------------------------------------------------

/**
 * Creates a reactive effect that fires handlers when rows enter, exit, or
 * update within a query result. Effects process deltas only — they do not
 * maintain or require the full materialised query result.
 *
 * @example
 * ```typescript
 * const effect = createEffect({
 *   query: (q) => q.from({ msg: messagesCollection })
 *     .where(({ msg }) => eq(msg.role, 'user')),
 *   onEnter: async (event) => {
 *     await generateResponse(event.value)
 *   },
 * })
 *
 * // Later: stop the effect
 * await effect.dispose()
 * ```
 */
export function createEffect<
  TRow extends object = Record<string, unknown>,
  TKey extends string | number = string | number,
>(config: EffectConfig<TRow, TKey>): Effect {
  const id = config.id ?? `live-query-effect-${++effectCounter}`

  // AbortController for signalling disposal to handlers
  const abortController = new AbortController()

  const ctx: EffectContext = {
    effectId: id,
    signal: abortController.signal,
  }

  // Track in-flight async handler promises so dispose() can await them
  const inFlightHandlers = new Set<Promise<void>>()
  let disposed = false

  // Callback invoked by the pipeline runner with each batch of delta events
  const onBatchProcessed = (events: Array<DeltaEvent<TRow, TKey>>) => {
    if (disposed) return
    if (events.length === 0) return

    // Batch handler
    if (config.onBatch) {
      try {
        const result = config.onBatch(events, ctx)
        if (result instanceof Promise) {
          const tracked = result.catch((error) => {
            reportError(error, events[0]!, config.onError)
          })
          trackPromise(tracked, inFlightHandlers)
        }
      } catch (error) {
        // For batch handler errors, report with first event as context
        reportError(error, events[0]!, config.onError)
      }
    }

    for (const event of events) {
      if (abortController.signal.aborted) break

      const handler = getHandlerForEvent(event, config)
      if (!handler) continue

      try {
        const result = handler(event, ctx)
        if (result instanceof Promise) {
          const tracked = result.catch((error) => {
            reportError(error, event, config.onError)
          })
          trackPromise(tracked, inFlightHandlers)
        }
      } catch (error) {
        reportError(error, event, config.onError)
      }
    }
  }

  // The dispose function is referenced by both the returned Effect object
  // and the onSourceError callback, so we define it first.
  let disposalPromise: Promise<void> | undefined
  const dispose = (): Promise<void> => {
    if (disposalPromise) return disposalPromise
    // Abort and source-release callbacks may synchronously call dispose again.
    // Publish the shared result before entering either user callback boundary.
    const completion = createDeferred<void>()
    const attempt = completion.promise
    disposalPromise = attempt
    disposed = true

    // Abort signal for in-flight handlers
    abortController.abort()

    void (async () => {
      // Tear down the pipeline (unsubscribe from sources, etc.)
      let cleanupFailed = false
      let cleanupError: unknown
      try {
        runner.dispose()
      } catch (error) {
        cleanupFailed = true
        cleanupError = error
      }

      // Wait for any in-flight async handlers to settle
      if (inFlightHandlers.size > 0) {
        await Promise.allSettled([...inFlightHandlers])
      }

      if (cleanupFailed) throw cleanupError
    })().then(completion.resolve, completion.reject)
    void attempt.then(
      () => {},
      () => {
        if (disposalPromise === attempt) disposalPromise = undefined
      },
    )
    return attempt
  }

  // Create and start the pipeline
  const runner = new EffectPipelineRunner<TRow, TKey>({
    query: config.query,
    skipInitial: config.skipInitial ?? false,
    onBatchProcessed,
    onSourceError: (error: Error) => {
      if (disposed) return

      if (config.onSourceError) {
        try {
          config.onSourceError(error)
        } catch (callbackError) {
          console.error(
            devBuild() && process.env.NODE_ENV !== `production`
              ? `[Effect '${id}'] onSourceError callback threw:`
              : codedMessage(220, { id }),
            callbackError,
          )
        }
      } else {
        console.error(
          devBuild() && process.env.NODE_ENV !== `production`
            ? `[Effect '${id}'] ${error.message}. Disposing effect.`
            : codedMessage(221, { id, message: error.message }),
        )
      }

      // Auto-dispose — the effect can no longer function
      void dispose().catch((cleanupError) => {
        console.error(
          devBuild() && process.env.NODE_ENV !== `production`
            ? `[Effect '${id}'] failed to dispose after a source error:`
            : codedMessage(222, { id }),
          cleanupError,
        )
      })
    },
  })
  try {
    runner.start()
  } catch (error) {
    try {
      runner.dispose()
    } catch (cleanupError) {
      console.error(
        devBuild() && process.env.NODE_ENV !== `production`
          ? `[Effect '${id}'] failed to dispose after a startup error:`
          : codedMessage(223, { id }),
        cleanupError,
      )
    }
    throw error
  }

  return {
    dispose,
    get disposed() {
      return disposed
    },
  }
}

// ---------------------------------------------------------------------------
// EffectPipelineRunner
// ---------------------------------------------------------------------------

interface EffectPipelineRunnerConfig<
  TRow extends object,
  TKey extends string | number,
> {
  query: EffectQueryInput<any>
  skipInitial: boolean
  onBatchProcessed: (events: Array<DeltaEvent<TRow, TKey>>) => void
  /** Called when a source collection enters error or cleaned-up state */
  onSourceError: (error: Error) => void
}

/**
 * Report how a held acquisition ends. A truncate replay rejects the
 * acquisition it supersedes and starts a replacement, which inherits the hold.
 */
function followHeldAcquisition(
  acquisition: Promise<unknown>,
  replacementAfterFailure: () => Promise<void> | undefined,
  handlers: {
    settled: (succeeded: boolean) => void
    replaced: (replacement: Promise<void>) => void
  },
): void {
  void acquisition.then(
    () => handlers.settled(true),
    () => {
      const replacement = replacementAfterFailure()
      if (replacement) handlers.replaced(replacement)
      else handlers.settled(false)
    },
  )
}

/**
 * Internal class that manages a D2 pipeline for effect delta processing.
 *
 * Sets up the IVM graph, subscribes to source collections, runs the graph
 * when changes arrive, and classifies output multiplicities into DeltaEvents.
 *
 * Unlike CollectionConfigBuilder, this does not publish results to a
 * Collection.
 */
class EffectPipelineRunner<TRow extends object, TKey extends string | number> {
  private readonly query: QueryIR
  private readonly queryCompareOptions: StringCollationConfig
  private readonly collections: Record<string, Collection<any, any, any>>
  private readonly collectionSources: ReturnType<
    typeof extractCollectionSources
  >

  private graph: D2 | undefined
  private inputs: Record<string, RootStreamBuilder<unknown>> | undefined
  private sourceWhereClauses: Map<string, BasicExpression<boolean>> | undefined

  // Mutable objects passed to compileQuery by reference.
  // The join compiler captures these references and reads them later when
  // the graph runs, so they must be populated before the first graph run.
  private readonly subscriptions = createSourceRecord<CollectionSubscription>()
  private readonly lazySourcesCallbacks =
    createSourceRecord<LazyCollectionCallbacks>()
  private readonly lazySources = new Set<string>()
  private readonly demand = new SubsetDemandController()
  // OrderBy optimization info populated by the compiler when limit is present
  private readonly optimizableOrderByCollections =
    createSourceRecord<OrderByOptimizationInfo>()

  // Ordered subscription state for cursor-based loading
  private readonly orderedLoaders = new Map<string, OrderedSourceLoader>()
  // An asynchronous authoritative repair may update the private D2 state in
  // several prefix/tie/refill steps. Keep their net Effect delta private until
  // the final participant settles, matching live-query Collection publication.
  private readonly pendingOrderedPublications = new Set<Promise<unknown>>()
  private readonly pendingInitialLoads = new Set<Promise<unknown>>()
  private orderedPublicationFailed = false

  // Subscription management
  private readonly unsubscribeCallbacks = new Set<() => void>()
  // Exact row last contributed to D2 per lexical source key.
  private readonly sentToD2RowsBySource = new Map<
    string,
    Map<string | number, Record<string, unknown>>
  >()

  // Output accumulator
  private pendingChanges: Map<unknown, EffectChanges<TRow>> = new Map()
  // Callback-visible result. Held repair deltas are classified against this
  // last coherent membership rather than their net multiplicity alone.
  private readonly publishedRows = new Map<unknown, TRow>()

  // skipInitial state
  private readonly skipInitial: boolean
  private initialLoadComplete = false

  // Scheduler integration
  private subscribedToAllCollections = false
  private readonly builderDependencies = new Set<GraphDependency>()

  // Reentrance guard
  private isGraphRunning = false
  private starting = false
  private disposed = false

  private readonly onBatchProcessed: (
    events: Array<DeltaEvent<TRow, TKey>>,
  ) => void
  private readonly onSourceError: (error: Error) => void

  constructor(config: EffectPipelineRunnerConfig<TRow, TKey>) {
    this.skipInitial = config.skipInitial
    this.onBatchProcessed = config.onBatchProcessed
    this.onSourceError = config.onSourceError

    // Parse query
    this.query = buildQueryFromConfig({ query: config.query })
    this.queryCompareOptions = extractCollectionFromSource(
      this.query,
    ).compareOptions

    // Extract source collections
    this.collections = extractCollectionsFromQuery(this.query)
    this.collectionSources = extractCollectionSources(this.query)

    // Compile the pipeline
    this.compilePipeline()
  }

  /** Compile the D2 graph and query pipeline */
  private compilePipeline(): void {
    this.graph = new D2()
    const inputs = createSourceRecord<RootStreamBuilder<unknown>>()
    for (const source of this.collectionSources) {
      inputs[source.sourceId] = this.graph.newInput<any>()
    }
    this.inputs = inputs

    const compilation = compileQuery(
      this.query,
      this.inputs as Record<string, KeyedStream>,
      this.collections,
      // These mutable objects are captured by reference. The join compiler
      // reads them later when the graph runs, so they must be populated
      // (in start()) before the first graph run.
      this.subscriptions,
      this.lazySourcesCallbacks,
      this.lazySources,
      this.optimizableOrderByCollections,
      () => {}, // setWindowFn (no-op — effects don't paginate)
    )

    this.sourceWhereClauses = compilation.sourceWhereClauses

    // Attach the output operator that accumulates changes
    compilation.pipeline.pipe(
      output((data) => {
        const messages = data.getInner()
        messages.reduce(accumulateEffectChanges<TRow>, this.pendingChanges)
      }),
    )

    this.graph.finalize()
  }

  private isDisposed(): boolean {
    return this.disposed
  }

  /** Subscribe to source collections and start processing */
  start(): void {
    this.starting = true
    if (this.collectionSources.length === 0) {
      // Nothing to subscribe to
      this.starting = false
      return
    }

    // When not skipping initial, we always process events immediately
    if (!this.skipInitial) {
      this.initialLoadComplete = true
    }

    // We need to defer initial data processing until ALL subscriptions are
    // created, because join pipelines look up subscriptions by alias during
    // the graph run. If we run the graph while some aliases are still missing,
    // the join tap operator will throw.
    //
    // Strategy: subscribe to each collection but buffer incoming changes.
    // After all subscriptions are in place, flush the buffers and switch to
    // direct processing mode.

    const pendingBuffers = new Map<
      string,
      Array<Array<ChangeMessage<any, string | number>>>
    >()

    for (const source of this.collectionSources) {
      if (this.isDisposed()) {
        this.starting = false
        return
      }

      const { sourceId, alias, collection } = source
      const collectionId = collection.id

      this.sentToD2RowsBySource.set(sourceId, new Map())

      // Discover dependencies: if source collection is itself a live query
      // collection, its builder must run first during transaction flushes.
      const dependencyBuilder = getCollectionBuilder(collection)
      if (dependencyBuilder) {
        this.builderDependencies.add(dependencyBuilder)
      }

      // Get where clause for this alias (for predicate push-down)
      const whereClause = this.sourceWhereClauses?.get(sourceId)
      const whereExpression = whereClause
        ? normalizeExpressionPaths(whereClause, alias)
        : undefined

      // Initialise buffer for this alias
      const buffer: Array<Array<ChangeMessage<any, string | number>>> = []
      pendingBuffers.set(sourceId, buffer)

      // Lazy aliases (marked by the join compiler) should NOT load initial state
      // eagerly — the join tap operator will load exactly the rows it needs on demand.
      // For on-demand collections, eager loading would trigger a full server fetch
      // for data that should be lazily loaded based on join keys.
      const isLazy = this.lazySources.has(sourceId)

      // Check if this alias has orderBy optimization (cursor-based loading)
      const orderByInfo = this.optimizableOrderByCollections[sourceId]

      const changeCallback = (
        changes: Array<ChangeMessage<any, string | number>>,
      ) => {
        const bufferedChanges = pendingBuffers.get(sourceId)
        if (bufferedChanges) bufferedChanges.push(changes)
        else this.handleSourceChanges(sourceId, changes)
      }

      // Subscribe to source changes
      const subscription = collection.subscribeChanges(changeCallback, {
        ...this.buildSubscriptionOptions(
          alias,
          isLazy,
          orderByInfo,
          whereExpression,
        ),
        onLoadSubsetError: ({ error }) => {
          this.onSourceError(normalizeError(error))
        },
        // subscribeChanges can report a result before it returns the
        // subscription, so read it from the record when a load fails.
        onLoadSubsetResult: (result) =>
          this.trackInitialLoad(
            result,
            () => this.subscriptions[sourceId]?.pendingTruncateReplacement,
          ),
      })

      // Store subscription immediately so the join compiler can find it
      this.subscriptions[sourceId] = subscription

      const unsubscribe = () => {
        delete this.subscriptions[sourceId]
        subscription.unsubscribe()
      }

      // subscribeChanges can synchronously report a source error and dispose
      // the runner before returning the subscription.
      if (this.isDisposed()) {
        unsubscribe()
        this.starting = false
        return
      }

      // Own the subscription before any ordered snapshot or lazy demand can
      // throw. A partially started effect has no handle for its caller to
      // dispose, so start() must be able to release every acquired source.
      this.unsubscribeCallbacks.add(unsubscribe)

      if (
        Object.values(this.optimizableOrderByCollections).some(
          (info) => info.joinedFilterSourceId === sourceId,
        )
      ) {
        this.unsubscribeCallbacks.add(
          subscription.on(`status:ready`, () => {
            if (!this.disposed) this.scheduleGraphRun()
          }),
        )
      }

      const lazyCallbacks = this.lazySourcesCallbacks[sourceId]
      if (lazyCallbacks) {
        lazyCallbacks.setDemand = (plan: LazyDemandPlan, keys: Set<unknown>) =>
          this.setDemand(subscription, plan, keys)
        for (const plan of lazyCallbacks.plans ?? []) {
          if (plan.initialKeys.size > 0) {
            lazyCallbacks.setDemand(plan, plan.initialKeys)
          }
        }
      }

      // For ordered aliases with an index, trigger the initial limited snapshot.
      // This loads only the top N rows rather than the entire collection.
      if (orderByInfo) {
        const loader = new OrderedSourceLoader(
          orderByInfo,
          subscription,
          alias,
          (result, holdPublication) => {
            const holdsInitialPublication =
              this.skipInitial && !this.initialLoadComplete
            if (holdPublication || holdsInitialPublication) {
              this.trackOrderedPublication(
                Promise.resolve(result),
                () => subscription.pendingTruncateReplacement,
              )
            }
          },
          () => false,
          undefined,
          () =>
            orderByInfo.joinedFilterSourceId !== undefined &&
            this.hasPendingJoinedWork(orderByInfo.joinedFilterSourceId),
        )
        this.orderedLoaders.set(sourceId, loader)
        loader.start()
      }

      const handleSourceCleanup = () => {
        if (this.disposed) return
        this.onSourceError(
          new Error(
            devBuild() && process.env.NODE_ENV !== `production`
              ? `Source collection '${collectionId}' was cleaned up while effect depends on it`
              : codedMessage(134, { collectionId }),
          ),
        )
      }

      // Listen for status changes on source collections
      const statusUnsubscribe = collection.on(`status:change`, (event) => {
        if (this.disposed) return

        const { status } = event

        // Source entered error state — effect can no longer function
        if (status === `error`) {
          this.onSourceError(
            new Error(
              devBuild() && process.env.NODE_ENV !== `production`
                ? `Source collection '${collectionId}' entered error state`
                : codedMessage(135, { collectionId }),
            ),
          )
          return
        }

        // Source was manually cleaned up — effect can no longer function
        if (status === `cleaned-up`) {
          handleSourceCleanup()
          return
        }

        // Track source readiness for skipInitial
        if (
          this.skipInitial &&
          !this.initialLoadComplete &&
          this.canCompleteInitialLoad()
        ) {
          this.initialLoadComplete = true
        }
      })
      this.unsubscribeCallbacks.add(statusUnsubscribe)
      const cleanupStartUnsubscribe =
        collection._onCleanupStart(handleSourceCleanup)
      // Registration reports an already-active cleanup synchronously. That
      // callback can dispose this runner before the unsubscribe handle exists.
      if (this.isDisposed()) {
        cleanupStartUnsubscribe()
        this.starting = false
        return
      }
      this.unsubscribeCallbacks.add(cleanupStartUnsubscribe)
    }

    // Mark as subscribed so the graph can start running
    this.subscribedToAllCollections = true

    // All subscriptions are now in place. Flush buffered changes by sending
    // data to D2 inputs first (without running the graph), then run the graph
    // once. This prevents intermediate join states from producing duplicates.
    //
    // We remove each alias from pendingBuffers *before* draining, which
    // switches that alias to direct-processing mode. Any new callbacks that
    // fire during the drain (e.g. from requestLimitedSnapshot) will go
    // through handleSourceChanges directly instead of being lost.
    for (const [sourceId, buffer] of pendingBuffers) {
      pendingBuffers.delete(sourceId)

      // Drain all buffered batches. Since we deleted the alias from
      // pendingBuffers above, any new changes arriving during drain go
      // through handleSourceChanges directly (not back into this buffer).
      for (const changes of buffer) {
        this.handleSourceChanges(sourceId, changes, false)
      }
    }

    // Initial graph run to process any synchronously-available data.
    // For skipInitial, this run's output is discarded (initialLoadComplete is still false).
    this.runGraph()

    // After the initial graph run, if all sources are ready,
    // mark initial load as complete so future events are processed.
    if (
      this.skipInitial &&
      !this.initialLoadComplete &&
      this.canCompleteInitialLoad()
    ) {
      this.initialLoadComplete = true
    }
    this.starting = false
  }

  /** Handle incoming changes from a source collection */
  private handleSourceChanges(
    sourceId: string,
    changes: Array<ChangeMessage<any, string | number>>,
    scheduleGraph = true,
  ): void {
    if (this.optimizableOrderByCollections[sourceId]) {
      this.orderedLoaders
        .get(sourceId)
        ?.onSourceChanges(changes, this.sentToD2RowsBySource.get(sourceId))
      changes = [...splitUpdates(changes)]
    }
    this.sendChangesToD2(sourceId, changes)
    if (scheduleGraph) this.scheduleGraphRun()
  }

  private setDemand(
    subscription: CollectionSubscription,
    plan: LazyDemandPlan,
    keys: Set<unknown>,
  ): void {
    let update
    try {
      update = this.demand.setDemand(subscription, plan, keys)
    } catch (error) {
      // The subscription error event already reports adapter failures and
      // disposes this effect. Do not let that query-local failure escape the
      // source commit, but keep unrelated graph errors visible.
      if (!Object.is(subscription.lastError, error)) throw error
      if (this.starting) throw error
      return
    }
    if (update.ready instanceof Promise) {
      this.trackInitialLoad(
        update.ready,
        () => subscription.pendingTruncateReplacement,
      )
      // Each segment reports its own failure through the subscription. Consume
      // the aggregate rejection so Promise.all does not create a second,
      // detached error channel.
      void update.ready.then(
        () => {
          if (!this.disposed) this.scheduleGraphRun()
        },
        () => {},
      )
    }
  }

  /**
   * Schedule a graph run via the transaction-scoped scheduler.
   *
   * When called within a transaction, the run is deferred until the
   * transaction flushes, coalescing multiple changes into a single graph
   * execution. Without a transaction, the graph runs immediately.
   *
   * Dependencies are discovered from source collections that are themselves
   * live query collections, ensuring parent queries run before effects.
   */
  private scheduleGraphRun(): void {
    scheduleQueryGraphRun(this, this.builderDependencies, () => {
      if (!this.disposed && this.subscribedToAllCollections) this.runGraph()
    })
  }

  /** Keep `skipInitial` suppression open until a pending initial load settles. */
  private trackInitialLoad(
    result: true | Promise<unknown>,
    replacementAfterFailure: () => Promise<void> | undefined,
  ): void {
    if (
      !(result instanceof Promise) ||
      this.initialLoadComplete ||
      this.disposed
    )
      return
    this.pendingInitialLoads.add(result)
    const release = () => {
      if (this.pendingInitialLoads.delete(result) && !this.disposed)
        this.scheduleGraphRun()
    }
    // A load failure disposes the Effect through onLoadSubsetError. Any other
    // rejection comes from an obsolete or aborted acquisition, so the hold
    // fails open: it releases unless a truncate replacement takes it over.
    followHeldAcquisition(result, replacementAfterFailure, {
      settled: release,
      replaced: (replacement) => {
        this.trackInitialLoad(replacement, replacementAfterFailure)
        release()
      },
    })
  }

  /** Hold Effect callback publication across one authoritative repair chain. */
  private trackOrderedPublication(
    promise: Promise<unknown>,
    replacementAfterFailure: () => Promise<void> | undefined,
  ): void {
    if (this.disposed) return
    if (this.pendingOrderedPublications.size === 0) {
      this.orderedPublicationFailed = false
    }
    this.pendingOrderedPublications.add(promise)
    const finish = (succeeded: boolean) => {
      if (!this.pendingOrderedPublications.delete(promise)) return
      if (!succeeded) this.orderedPublicationFailed = true
      if (
        succeeded &&
        !this.orderedPublicationFailed &&
        this.pendingOrderedPublications.size === 0 &&
        !this.disposed
      ) {
        // The repair chain already drove its private D2 state to quiescence.
        // Run once more to publish the accumulated net delta.
        this.scheduleGraphRun()
      }
    }
    // A failed repair chain fails closed until a fresh chain starts. A
    // replacement settles after replay rows reach this Effect's private D2
    // state, so it takes over the same publication hold.
    followHeldAcquisition(promise, replacementAfterFailure, {
      settled: finish,
      replaced: (replacement) => {
        if (this.pendingOrderedPublications.delete(promise) && !this.disposed)
          this.trackOrderedPublication(replacement, replacementAfterFailure)
      },
    })
  }

  /**
   * Send changes to the D2 input for the given lexical source.
   * Returns the number of multiset entries sent.
   */
  private sendChangesToD2(
    sourceId: string,
    changes: Array<ChangeMessage<any, string | number>>,
  ): number {
    if (this.disposed || !this.inputs || !this.graph) return 0

    const input = this.inputs[sourceId]
    if (!input) return 0

    const sentRows = this.sentToD2RowsBySource.get(sourceId)!
    const reconciled = reconcileChangesForD2(changes, sentRows)

    return sendChangesToInput(input, reconciled)
  }

  /**
   * Run the D2 graph until quiescence, then emit accumulated events once.
   *
   * All output across the entire while-loop is accumulated into a single
   * batch so that users see one `onBatchProcessed` invocation per scheduler
   * run, even when ordered loading causes multiple graph steps.
   */
  private runGraph(): void {
    if (this.isGraphRunning || this.disposed || !this.graph) return

    this.isGraphRunning = true
    try {
      if (!this.graph.pendingWork()) this.loadMoreIfNeeded()
      // Ordered refill can also dispose the runner between graph steps.
      while (!this.isDisposed() && this.graph.pendingWork()) {
        this.graph.run()
        // A handler (via onBatchProcessed) or source error callback may have
        // called dispose() during graph.run(). Stop early to avoid operating
        // on stale state. TS narrows disposed to false from the guard above
        // but it can change during graph.run() via callbacks.
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (this.disposed) break
        // After each step, check if ordered queries need more data.
        // loadMoreIfNeeded may send data to D2 inputs (via requestLimitedSnapshot),
        // causing pendingWork() to return true for the next iteration.
        this.loadMoreIfNeeded()
      }
      // Emit all accumulated events once the graph reaches quiescence
      this.flushPendingChanges()
      if (
        this.skipInitial &&
        !this.initialLoadComplete &&
        this.canCompleteInitialLoad()
      ) {
        this.initialLoadComplete = true
      }
    } finally {
      this.isGraphRunning = false
    }
  }

  /** Classify accumulated changes into DeltaEvents and invoke the callback */
  private flushPendingChanges(): void {
    if (this.pendingChanges.size === 0) return

    if (
      this.orderedPublicationFailed ||
      this.pendingOrderedPublications.size > 0
    ) {
      return
    }

    if (this.hasPendingJoinedFilterWork()) {
      return
    }

    const shouldPublish = !this.skipInitial || this.initialLoadComplete
    const tracksPublishedRows = this.orderedLoaders.size > 0

    const events: Array<DeltaEvent<TRow, TKey>> = []

    for (const [key, changes] of this.pendingChanges) {
      const event = classifyDelta<TRow, TKey>(
        key as TKey,
        changes,
        tracksPublishedRows ? this.publishedRows : undefined,
      )
      if (event) {
        if (tracksPublishedRows) {
          if (event.type === `exit`) this.publishedRows.delete(key)
          else this.publishedRows.set(key, event.value)
        }
        if (shouldPublish) events.push(event)
      }
    }

    this.pendingChanges = new Map()

    if (events.length > 0) {
      this.onBatchProcessed(events)
    }
  }

  /** Check the source-readiness part of initial readiness for every source. */
  private sourcesPermitInitialReadiness(): boolean {
    return this.collectionSources.every(({ collection }) =>
      sourcePermitsInitialReadiness(collection),
    )
  }

  /** Initial callbacks start only after every source served its initial data. */
  private canCompleteInitialLoad(): boolean {
    return (
      !this.orderedPublicationFailed &&
      this.pendingOrderedPublications.size === 0 &&
      this.pendingInitialLoads.size === 0 &&
      !this.hasPendingJoinedFilterWork() &&
      this.sourcesPermitInitialReadiness()
    )
  }

  private hasPendingJoinedFilterWork(): boolean {
    return Object.values(this.optimizableOrderByCollections).some(
      (info) =>
        info.joinedFilterSourceId !== undefined &&
        this.hasPendingJoinedWork(info.joinedFilterSourceId),
    )
  }

  /**
   * Build subscription options for an alias based on whether it uses ordered
   * loading, is lazy, or should pass orderBy/limit hints.
   */
  private buildSubscriptionOptions(
    alias: string,
    isLazy: boolean,
    orderByInfo: OrderByOptimizationInfo | undefined,
    whereExpression: BasicExpression<boolean> | undefined,
  ): {
    includeInitialState?: boolean
    whereExpression?: BasicExpression<boolean>
    orderBy?: any
    limit?: number
  } {
    if (this.query.limit === 0) {
      return { includeInitialState: false, whereExpression }
    }

    // Ordered aliases explicitly disable initial state — data is loaded
    // via requestLimitedSnapshot/requestSnapshot after subscription setup.
    if (orderByInfo) {
      return { includeInitialState: false, whereExpression }
    }

    const includeInitialState = !isLazy

    // For unordered subscriptions, pass orderBy/limit hints so on-demand
    // collections can optimise server-side fetching.
    const hints = computeSubscriptionOrderByHints(
      this.query,
      alias,
      this.queryCompareOptions,
    )

    return {
      includeInitialState,
      whereExpression,
      ...(hints.orderBy ? { orderBy: hints.orderBy } : {}),
      ...(hints.limit !== undefined ? { limit: hints.limit } : {}),
    }
  }

  /**
   * After each graph run step, check if any ordered query's topK operator
   * needs more data. If so, load more rows via requestLimitedSnapshot.
   */
  private loadMoreIfNeeded(): void {
    for (const loader of this.orderedLoaders.values()) {
      try {
        loader.loadMore()
      } catch (error) {
        if (
          !this.disposed &&
          !Object.values(this.subscriptions).some((subscription) =>
            Object.is(subscription.lastError, error),
          )
        )
          throw error
      }
    }
  }

  private hasPendingJoinedWork(joinedSourceId: string): boolean {
    return hasPendingJoinedSourceWork(
      joinedSourceId,
      this.lazySourcesCallbacks,
      this.subscriptions,
      (planId) => this.demand.hasPendingDemand(planId),
    )
  }

  /** Tear down subscriptions and clear state */
  dispose(): void {
    if (this.disposed) return
    this.disposed = true
    this.subscribedToAllCollections = false

    // Release every source in one attempt; the first failure wins after the
    // peers finish. A reentrant dispose returns at the guard above, so this
    // call still owns each release exactly once.
    try {
      runAllCallbacks(this.unsubscribeCallbacks)
    } finally {
      this.unsubscribeCallbacks.clear()
      this.clearPipelineState()
    }
  }

  private clearPipelineState(): void {
    this.sentToD2RowsBySource.clear()
    this.pendingChanges.clear()
    this.publishedRows.clear()
    this.pendingOrderedPublications.clear()
    this.pendingInitialLoads.clear()
    this.orderedPublicationFailed = false
    this.lazySources.clear()
    this.demand.clear()
    this.builderDependencies.clear()
    for (const loader of this.orderedLoaders.values()) loader.dispose()
    this.orderedLoaders.clear()

    // Clear mutable objects
    for (const key of Object.keys(this.lazySourcesCallbacks)) {
      delete this.lazySourcesCallbacks[key]
    }
    for (const key of Object.keys(this.optimizableOrderByCollections)) {
      delete this.optimizableOrderByCollections[key]
    }

    // graph.run() keeps its own stack reference. The disposed guard prevents
    // another step or new input; clearing our references does not destroy it.
    this.graph = undefined
    this.inputs = undefined
    this.sourceWhereClauses = undefined
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getHandlerForEvent<TRow extends object, TKey extends string | number>(
  event: DeltaEvent<TRow, TKey>,
  config: EffectConfig<TRow, TKey>,
): EffectEventHandler<TRow, TKey> | undefined {
  switch (event.type) {
    case `enter`:
      return config.onEnter
    case `exit`:
      return config.onExit
    case `update`:
      return config.onUpdate
  }
}

/**
 * Accumulate D2 output multiplicities into per-key effect changes.
 * Tracks both insert values (new) and delete values (old) separately
 * so that update and exit events can include previousValue.
 */
function accumulateEffectChanges<T>(
  acc: Map<unknown, EffectChanges<T>>,
  [[key, tupleData], multiplicity]: [
    [unknown, [any, string | undefined]],
    number,
  ],
): Map<unknown, EffectChanges<T>> {
  const [value] = tupleData as [T, string | undefined]

  const changes: EffectChanges<T> = acc.get(key) || {
    deletes: 0,
    inserts: 0,
  }

  if (multiplicity < 0) {
    changes.deletes += Math.abs(multiplicity)
    // Keep only the first delete value — this is the pre-batch state
    changes.deleteValue ??= value
  } else if (multiplicity > 0) {
    changes.inserts += multiplicity
    // Always overwrite with the latest insert — this is the post-batch state
    changes.insertValue = value
  }

  acc.set(key, changes)
  return acc
}

/** Interpret a D2 batch against callback-visible state when a window holds it. */
function classifyDelta<TRow extends object, TKey extends string | number>(
  key: TKey,
  changes: EffectChanges<TRow>,
  publishedRows?: ReadonlyMap<unknown, TRow>,
): DeltaEvent<TRow, TKey> | undefined {
  const { inserts, deletes, insertValue, deleteValue } = changes
  const wasPresent = publishedRows ? publishedRows.has(key) : deletes > 0
  const previousValue = publishedRows?.get(key) ?? deleteValue
  const isPresent = publishedRows
    ? Number(wasPresent) + inserts - deletes > 0
    : inserts > 0

  if (!wasPresent && isPresent) {
    // Row entered the query result
    return { type: `enter`, key, value: insertValue! }
  }

  if (wasPresent && !isPresent) {
    // Row exited the query result — value is the exiting value,
    // previousValue is omitted (it would be identical to value)
    return { type: `exit`, key, value: previousValue ?? deleteValue! }
  }

  if (
    wasPresent &&
    isPresent &&
    insertValue !== undefined &&
    (!publishedRows || !deepEquals(previousValue, insertValue))
  ) {
    // Row updated within the query result
    return {
      type: `update`,
      key,
      value: insertValue,
      previousValue: previousValue!,
    }
  }

  // The row was absent both before and after the held delta, or its final
  // value is equal to the last callback-visible value.
  return undefined
}

/** Track a promise in the in-flight set, automatically removing on settlement */
function trackPromise(
  promise: Promise<void>,
  inFlightHandlers: Set<Promise<void>>,
): void {
  inFlightHandlers.add(promise)
  const finish = () => {
    inFlightHandlers.delete(promise)
  }
  void promise.then(finish, finish)
}

/** Report an error to the onError callback or console */
function reportError<TRow extends object, TKey extends string | number>(
  error: unknown,
  event: DeltaEvent<TRow, TKey>,
  onError?: (error: Error, event: DeltaEvent<TRow, TKey>) => void,
): void {
  const normalised = normalizeError(error)
  if (onError) {
    try {
      onError(normalised, event)
    } catch (onErrorError) {
      // Don't let onError errors propagate
      console.error(
        devBuild() && process.env.NODE_ENV !== `production`
          ? `[Effect] Error in onError handler:`
          : codedMessage(224),
        onErrorError,
      )
      console.error(
        devBuild() && process.env.NODE_ENV !== `production`
          ? `[Effect] Original error:`
          : codedMessage(225),
        normalised,
      )
    }
  } else {
    console.error(
      devBuild() && process.env.NODE_ENV !== `production`
        ? `[Effect] Unhandled error in handler:`
        : codedMessage(226),
      normalised,
    )
  }
}
